// libraries
import { useRef, useState } from 'react';
import { Input, Select } from 'antd';
import styled from 'styled-components';

// services
import { txt } from '../../locales/i18n';

export type AddressSearchBarProps = {
	onSelect: (address: Address | null) => void;
};

type AddressItem = {
	id: string;
	adressebetegnelse: string;
	adgangsadresse: any;
};

export type Address = {
	postalCode: number;
	city: string;
	name: string;
	lat: number;
	lng: number;
};

export const AddressSearchBar = (props: AddressSearchBarProps) => {
	const [postalCode, setPostalCode] = useState<number>();
	const [addresses, setAddresses] = useState<AddressItem[]>([]);
	const searchTimeout = useRef<NodeJS.Timeout | null>(null);

	const handleAddressSearch = async (value: string) => {
		if (searchTimeout.current !== null) {
			clearTimeout(searchTimeout.current);
		}

		const url = `https://api.dataforsyningen.dk/adresser?q=${value}&postnr=${postalCode}&per_side=10`;

		searchTimeout.current = setTimeout(async () => {
			const response: Response = await fetch(url);
			const json = await response.json();
			setAddresses(json);
		}, 400);
	};

	const handleAddressSelection = (addressName: string) => {
		const address: AddressItem | undefined = addresses.find(({ adressebetegnelse }) => {
			return adressebetegnelse === addressName;
		});

		if (address === undefined) return;

		const postalCode = Number(address.adgangsadresse.postnummer.nr);
		const city = address.adgangsadresse.postnummer.navn;
		const name = address.adressebetegnelse.replace(`, ${postalCode} ${city}`, '');
		const [lng, lat] = address.adgangsadresse.adgangspunkt.koordinater;

		props.onSelect({ postalCode, city, name, lat, lng });
	};

	const handlePostalCodeChange = ({ target }: any) => {
		setAddresses([]);
		setPostalCode(Number(target.value));
	};

	const handleAddressClear = () => {
		setAddresses([]);
		props.onSelect(null);
	};

	return (
		<StyledAddressSearchBar>
			<Input
				type="number"
				placeholder={txt('global.postal code')}
				onChange={handlePostalCodeChange}
				className="postalcode-input"
			/>

			<Select
				allowClear
				showSearch
				showArrow={false}
				onSearch={handleAddressSearch}
				onSelect={handleAddressSelection}
				placeholder={txt('global.search')}
				disabled={String(postalCode).length !== 4}
				className="address-input"
				onClear={handleAddressClear}
			>
				<Select.Option key="-1" value="" disabled>
					{txt('global.address')}
				</Select.Option>

				{addresses.map((address) => (
					<Select.Option key={address.id} value={address.adressebetegnelse}>
						{address.adressebetegnelse}
					</Select.Option>
				))}
			</Select>
		</StyledAddressSearchBar>
	);
};

const StyledAddressSearchBar = styled.main`
	display: flex;

	.postalcode-input {
		width: 12ch;
	}

	.address-input {
		flex: 1;
	}
`;
